import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { watchContext } from '../../base/context';
import { ElementLogger } from '../../base/logger';
import { DEV_MODE } from '../../global/env';
import { mediaContext } from '../../media';
import { setAttribute } from '../../utils/dom';
import { bufferingIndicatorElementStyles } from './styles';
export const BUFFERING_INDICATOR_ELEMENT_TAG_NAME = 'vds-buffering-indicator';
/**
 * Display an indicator when either the provider/media is booting or media playback has
 * stopped because of a lack of temporary data.
 *
 * 💡 The following attributes are updated for your styling needs:
 *
 * - `media-can-play`: Applied when media can begin playback.
 * - `media-waiting`: Applied when playback has stopped because of a lack of temporary data.
 * - `media-ended`: Applied when playback has reached the end.
 *
 * @tagname vds-buffering-indicator
 * @slot Used to pass in the content to be displayed while buffering.
 * @example
 * ```html
 * <vds-buffering-indicator>
 *   <!-- ... -->
 * </vds-buffering-indicator>
 * ```
 * @example
 * ```css
 * vds-buffering-indicator {
 *   opacity: 0;
 *   transition: opacity 0.3s ease-out;
 *   transition-delay: 500ms;
 * }
 *
 * vds-buffering-indicator[media-waiting],
 * vds-buffering-indicator:not([media-can-play]) {
 *   opacity: 1;
 * }
 * ```
 */
export class BufferingIndicatorElement extends LitElement {
    constructor() {
        super(...arguments);
        /* c8 ignore next */
        this._logger = DEV_MODE && new ElementLogger(this);
    }
    static get styles() {
        return [bufferingIndicatorElementStyles];
    }
    render() {
        return html `<slot></slot>`;
    }
    _handleCanPlayContextUpdate(canPlay) {
        setAttribute(this, 'media-can-play', canPlay);
    }
    _handleWaitingContextUpdate(waiting) {
        setAttribute(this, 'media-waiting', waiting);
    }
    _handleEndedContextUpdate(ended) {
        setAttribute(this, 'media-ended', ended);
    }
}
__decorate([
    watchContext(mediaContext.canPlay)
], BufferingIndicatorElement.prototype, "_handleCanPlayContextUpdate", null);
__decorate([
    watchContext(mediaContext.waiting)
], BufferingIndicatorElement.prototype, "_handleWaitingContextUpdate", null);
__decorate([
    watchContext(mediaContext.ended)
], BufferingIndicatorElement.prototype, "_handleEndedContextUpdate", null);
